import { AlertCircle, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import SEO from "@/components/site/SEO";
import SectionRenderer from "@/components/sections/SectionRenderer";
import { getPage } from "@/lib/api";
import { useAsync } from "@/lib/useAsync";
import { buildBreadcrumbs } from "@/lib/breadcrumbs";

/**
 * Generic CMS-driven page. Loads /api/pages/{slug} and renders sections.
 * `banner` prop can be set to "page" for inner pages whose first section
 * should be forced to render as a PageBanner.
 * Breadcrumbs are auto-computed from the current URL and passed to any
 * PageBanner the renderer produces.
 */
export default function CMSPage({ slug, banner, fallbackTitle }) {
  const { data: page, loading, error } = useAsync(() => getPage(slug), [slug]);
  const { pathname } = useLocation();
  const breadcrumbs = buildBreadcrumbs(pathname);

  if (loading) {
    return (
      <div
        data-testid={`page-${slug}-loading`}
        className="min-h-[60vh] flex items-center justify-center bg-mist-off"
      >
        <Loader2 className="h-8 w-8 text-mist-navy animate-spin" aria-hidden />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div
        data-testid={`page-${slug}-error`}
        className="min-h-[60vh] flex flex-col items-center justify-center gap-3 bg-mist-off text-mist-ink"
      >
        <AlertCircle className="h-8 w-8 text-mist-navy" aria-hidden />
        <div className="text-lg font-display text-mist-navy">Content unavailable</div>
        <div className="text-sm text-mist-ink/70">
          We could not load this page. Please try again shortly.
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO seo={page.seo} fallbackTitle={fallbackTitle || page.title} />
      <div data-testid={`page-${slug}`}>
        <SectionRenderer sections={page.sections || []} banner={banner} breadcrumbs={breadcrumbs} />
      </div>
    </>
  );
}
